import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";
import GuardianConsentSection from "@/components/GuardianConsentSection";
import { emptyGuardian, guardianPayload, resolveGuardianSection, validateGuardian } from "@/lib/guardian";
import { Check, AlertCircle, FileText, Calendar } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

/**
 * PDFFormViewer - Patient-facing PDF form viewer with acknowledgement and signature
 * 
 * Displays uploaded PDF forms for patients to view, acknowledge, and sign.
 */
export default function PDFFormViewer({
  template,
  patientId,
  appointmentId,
  patientName,
  clinicName,
  clinicLogo,
  onComplete,
  onCancel,
  pdfFetchPath, // optional override for the PDF source (public patient link)
  pdfClient, // optional axios instance (unauthenticated for public links)
  submitForm, // optional override: async (payload) => {}
  minorInfo,
  guardianSection: guardianSectionProp,
  successNote = "Returning to reception...",
  autoCloseOnSuccess = true,
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [consented, setConsented] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [guardian, setGuardian] = useState(emptyGuardian());
  const [guardianSignature, setGuardianSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const signatureRef = useRef(null);
  const guardianSignatureRef = useRef(null);
  const pdfContainerRef = useRef(null);
  const iframeRef = useRef(null);

  // Patient-facing mode: hide the clinic shell while this form is open
  useEffect(() => {
    document.body.classList.add("lf-device-form-open");
    return () => document.body.classList.remove("lf-device-form-open");
  }, []);

  const guardianSection = resolveGuardianSection(template, minorInfo, guardianSectionProp);
  const guardianRequired = Boolean(guardianSection.show && guardianSection.required);

  const requiresConsent = !!template?.pdf_consent_text;
  const requiresSignature = template?.pdf_requires_signature !== false;
  const patientSignatureRequired = requiresSignature && !guardianRequired;
  const acknowledgementText = template?.pdf_acknowledgement_text || "I confirm that I have read and understood this document.";
  const consentText = template?.pdf_consent_text;

  // Fetch PDF via authenticated API and create blob URL
  useEffect(() => {
    let blobUrl = null;
    const fetchPdf = async () => {
      if (!template?.id) return;
      
      setPdfLoading(true);
      setPdfError(false);
      
      try {
        const response = await (pdfClient || api).get(pdfFetchPath || `/forms/pdf/${template.id}`, {
          responseType: 'blob'
        });
        blobUrl = URL.createObjectURL(response.data);
        setPdfBlobUrl(blobUrl);
      } catch (err) {
        console.error("Failed to load PDF:", err);
        setPdfError(true);
      } finally {
        setPdfLoading(false);
      }
    };
    
    fetchPdf();
    
    // Cleanup blob URL on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [template?.id]);

  // Track scroll position
  const handleScroll = () => {
    if (pdfContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = pdfContainerRef.current;
      // Consider "scrolled to bottom" if within 50px of bottom
      if (scrollHeight - scrollTop - clientHeight < 50) {
        setScrolledToBottom(true);
      }
    }
  };

  const validateForm = () => {
    if (!acknowledged) {
      setValidationError("Please confirm you have read and understood the document.");
      return false;
    }
    if (requiresConsent && !consented) {
      setValidationError("Please provide your consent to continue.");
      return false;
    }
    if (patientSignatureRequired && (!signatureData || !signatureData.data)) {
      setValidationError("Please provide your signature to complete this form.");
      return false;
    }
    const latestGuardianSignature = guardianSignatureRef.current?.getSignatureData() || guardianSignature;
    const guardianError = validateGuardian(guardian, guardianRequired, latestGuardianSignature);
    if (guardianError) {
      setValidationError(guardianError);
      return false;
    }
    setValidationError("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      let finalSignature = signatureData;
      if (signatureRef.current) {
        const sigData = signatureRef.current.getSignatureData();
        if (sigData) {
          finalSignature = sigData;
        }
      }

      const today = new Date().toISOString().split("T")[0];
      
      const payload = {
        template_id: template.id,
        patient_id: patientId,
        appointment_id: appointmentId,
        responses: {
          acknowledgement: acknowledged,
          acknowledgement_text: acknowledgementText,
          consent: requiresConsent ? consented : null,
          consent_text: consentText || null,
          date_signed: today,
        },
        status: "completed",
        signature_type: finalSignature?.type || null,
        signature_data: finalSignature?.type === "drawn" ? finalSignature.data : null,
        typed_signature: finalSignature?.type === "typed" ? finalSignature.data : null,
        signature: finalSignature?.type === "typed" ? finalSignature.data : null,
        completed_on_device: true,
        guardian: guardianSection.show
          ? guardianPayload(guardian, guardianSignatureRef.current?.getSignatureData() || guardianSignature)
          : null,
      };

      await (submitForm ? submitForm(payload) : api.post("/forms/submissions", payload));
      setShowSuccess(true);

      // Auto-close after 2 seconds
      if (autoCloseOnSuccess) {
        setTimeout(() => {
          onComplete?.();
        }, 2000);
      } else {
        onComplete?.();
      }
    } catch (err) {
      setValidationError("There was a problem submitting the form. Please try again.");
    }
    setSubmitting(false);
  };

  const handleSignatureChange = (data) => {
    setSignatureData(data.isEmpty ? null : data);
    if (validationError && data.data) {
      setValidationError("");
    }
  };

  // Success state
  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-[10000] bg-[#FAF9F6] flex flex-col items-center justify-center p-6" data-testid="form-submitted-state">
        <div className="w-20 h-20 rounded-full bg-[#205B5A]/10 flex items-center justify-center mb-6">
          <Check className="w-10 h-10 text-[#205B5A]" strokeWidth={2} />
        </div>
        <h1 className="text-2xl font-medium text-[#1A1B1A] mb-2" style={H}>
          Form submitted
        </h1>
        <p className="text-sm text-[#757675] text-center">
          Thank you for completing the form, {patientName?.split(" ")[0] || "Patient"}.
        </p>
        <p className="text-xs text-[#757675] mt-4">
          {successNote}
        </p>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[10000] bg-[#FAF9F6] flex flex-col"
      data-testid="pdf-form-viewer"
    >
      {/* Header */}
      <div className="bg-white border-b border-[#1A1B1A]/[0.06] px-4 sm:px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          {clinicLogo ? (
            <img src={clinicLogo} alt="" className="h-8 w-auto" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-[#205B5A]/10 flex items-center justify-center">
              <FileText className="w-4 h-4 text-[#205B5A]" />
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-[#1A1B1A]" style={H}>
              {clinicName || "Clinic"}
            </p>
            <p className="text-xs text-[#757675]">{template?.name || "Document"}</p>
          </div>
        </div>
        {onCancel && (
        <button
          onClick={onCancel}
          className="text-xs text-[#757675] hover:text-[#1A1B1A] px-3 py-1.5 rounded-lg hover:bg-[#FAF9F6] border border-[#1A1B1A]/10"
          data-testid="pdf-form-cancel"
        >
          Return to reception
        </button>
        )}
      </div>

      {/* Patient greeting */}
      <div className="bg-white px-4 sm:px-6 py-3 border-b border-[#1A1B1A]/[0.06]">
        <p className="text-sm text-[#1A1B1A]">
          Hello, <span className="font-medium">{patientName || "Patient"}</span>. Please review the document below, then acknowledge and sign.
        </p>
        {guardianRequired && (
          <p className="mt-2 text-sm text-[#205B5A] bg-[#205B5A]/[0.06] border border-[#205B5A]/20 rounded-xl px-3 py-2" data-testid="minor-patient-notice">
            {guardianSection.notice}
          </p>
        )}
      </div>

      {/* Validation error */}
      {validationError && (
        <div className="bg-red-50 px-4 sm:px-6 py-3 border-b border-red-100" data-testid="pdf-form-error">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-600">{validationError}</p>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
        {/* PDF Viewer */}
        <div
          ref={pdfContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-auto bg-[#3d3d3d] p-4"
          data-testid="pdf-container"
        >
          {pdfLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-3" />
                <p className="text-white/60 text-sm">Loading document...</p>
              </div>
            </div>
          ) : pdfBlobUrl ? (
            <div className="max-w-4xl mx-auto">
              {/* PDF iframe using authenticated blob URL */}
              <iframe
                ref={iframeRef}
                src={`${pdfBlobUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                className="w-full bg-white rounded-lg shadow-lg"
                style={{ height: "calc(100vh - 300px)", minHeight: "500px" }}
                title={template?.name || "PDF Document"}
                data-testid="pdf-iframe"
              />
            </div>
          ) : pdfError ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-white/50 mx-auto mb-4" />
                <p className="text-white/80 mb-4">Unable to load the PDF document.</p>
                <p className="text-white/60 text-sm">Please try again or contact support.</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-white/60">No PDF available</p>
            </div>
          )}
        </div>

        {/* Acknowledgement & Signature Panel */}
        <div className="w-full lg:w-[400px] bg-white border-t lg:border-t-0 lg:border-l border-[#1A1B1A]/[0.06] flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
            <div>
              <h3 className="text-sm font-medium text-[#1A1B1A] mb-3" style={H}>Acknowledgement & Signature</h3>
              <p className="text-xs text-[#757675]">
                Please confirm you have read the document and provide your signature.
              </p>
            </div>

            {/* Acknowledgement checkbox */}
            <label
              className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                acknowledged
                  ? "border-[#205B5A] bg-[#205B5A]/5"
                  : "border-[#1A1B1A]/10 hover:border-[#205B5A]/30"
              }`}
              data-testid="acknowledgement-checkbox"
            >
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => {
                  setAcknowledged(e.target.checked);
                  if (validationError) setValidationError("");
                }}
                className="w-5 h-5 rounded border-[#1A1B1A]/20 text-[#205B5A] mt-0.5 flex-shrink-0"
              />
              <span className="text-sm text-[#1A1B1A] leading-relaxed">
                {acknowledgementText}
              </span>
            </label>

            {/* Consent checkbox (if configured) */}
            {requiresConsent && (
              <label
                className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                  consented
                    ? "border-[#205B5A] bg-[#205B5A]/5"
                    : "border-[#1A1B1A]/10 hover:border-[#205B5A]/30"
                }`}
                data-testid="consent-checkbox"
              >
                <input
                  type="checkbox"
                  checked={consented}
                  onChange={(e) => {
                    setConsented(e.target.checked);
                    if (validationError) setValidationError("");
                  }}
                  className="w-5 h-5 rounded border-[#1A1B1A]/20 text-[#205B5A] mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-[#1A1B1A] leading-relaxed">
                  {consentText}
                </span>
              </label>
            )}

            {/* Signature */}
            {requiresSignature && (
              <div>
                <SignaturePad
                  ref={signatureRef}
                  label={guardianRequired ? "Patient signature (optional)" : "Your signature"}
                  required={!guardianRequired}
                  onChange={handleSignatureChange}
                  initialMode="draw"
                  compact={true}
                />
                {guardianRequired && (
                  <p className="mt-2 text-xs text-[#4A4B4A]" data-testid="patient-signature-optional-note">
                    {guardianSection.patient_signature_note}
                  </p>
                )}
                {/* Date signed indicator - read-only, recorded automatically using clinic timezone */}
                <div className="mt-3 flex items-center gap-2 text-sm text-[#757675] bg-[#FAF9F6] rounded-xl px-3 py-2.5" data-testid="pdf-date-signed-indicator">
                  <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Date signed: <strong className="text-[#1A1B1A]">{new Date().toLocaleDateString("en-GB")}</strong> <span className="text-[#9A9B9A] text-xs">(auto)</span></span>
                </div>
              </div>
            )}

            <GuardianConsentSection
              section={guardianSection}
              value={guardian}
              onChange={(g) => { setGuardian(g); if (validationError) setValidationError(""); }}
              signatureRef={guardianSignatureRef}
              onSignatureChange={(data) => {
                setGuardianSignature(data.isEmpty ? null : data);
                if (validationError && data.data) setValidationError("");
              }}
            />
          </div>

          {/* Submit button */}
          <div className="p-4 sm:p-6 border-t border-[#1A1B1A]/[0.06] bg-[#FAF9F6]">
            <button
              onClick={handleSubmit}
              disabled={submitting || !acknowledged || (requiresConsent && !consented)}
              className="w-full bg-[#205B5A] text-white py-3 rounded-xl text-sm font-medium hover:bg-[#174544] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="pdf-form-submit"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Submit form
                </>
              )}
            </button>
            <p className="text-[10px] text-[#757675] text-center mt-2.5" data-testid="form-audit-privacy-note">
              Your information is stored securely and used only for the purpose of providing clinical care.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
